import dayjs from 'dayjs';
import 'dayjs/locale/ur';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { en } from './en';
import { ur } from './ur';

/**
 * Shape of one language pack, derived from the English file. Typing ur.ts as this makes a missing
 * or misspelled Urdu key a compile error instead of a runtime fallback to English.
 */
export type Translations = {
  [Section in keyof typeof en]: { [Key in keyof (typeof en)[Section]]: string };
};

export type Language = 'en' | 'ur';

export const LANGUAGE_KEY = 'ims.language';

/** Urdu is right-to-left; the whole layout mirrors, not just the text. */
export const isRtl = (lng: string) => lng === 'ur';

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      ur: { translation: ur },
    },
    fallbackLng: 'en',
    supportedLngs: ['en', 'ur'],
    detection: {
      // Remembered per browser, so staff who work in Urdu are not reset on every visit.
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_KEY,
      caches: ['localStorage'],
    },
    interpolation: {
      // React escapes for us; double-escaping mangles the em dashes used throughout the copy.
      escapeValue: false,
    },
  });

/**
 * Applies the language to everything outside React's tree:
 *
 * - `dir` and `lang` on <html>, so the layout mirrors and screen readers announce Urdu correctly
 * - dayjs's global locale, so month names in formatted dates follow suit — without this the UI
 *   reads Urdu but every date still says "20 Aug 2026"
 *
 * Note apiDate() in utils/format.ts deliberately does NOT go through the locale: it emits
 * YYYY-MM-DD for the API, which must stay machine-readable whatever the user is reading.
 */
export function applyLanguage(lng: string) {
  const root = document.documentElement;
  root.setAttribute('lang', lng);
  root.setAttribute('dir', isRtl(lng) ? 'rtl' : 'ltr');
  dayjs.locale(isRtl(lng) ? 'ur' : 'en');
}

applyLanguage(i18n.language);
i18n.on('languageChanged', applyLanguage);

export default i18n;
